import React from "react";
import { Box, Typography, Button } from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { getAuthHeaders } from "../../utils/authToken";

const ChatMessage = ({ message, colors, fontConfig }) => {
  const isUser = message.role === "user";

  const handleDownload = async () => {
    if (!message.file) return;
    try {
      const resp = await fetch(
        `${process.env.REACT_APP_URL}/api/v1/chat-files/${message.file.fileId}`,
        { headers: getAuthHeaders() }
      );
      if (!resp.ok) throw new Error("Download failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = message.file.name || "download";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      alert("File expired or unavailable. Please export again.");
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-start",
        mb: 1.5,
        gap: 1,
        flexDirection: isUser ? "row-reverse" : "row",
        alignItems: "flex-start",
      }}
    >
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: isUser ? colors.greenAccent[500] : colors.blueAccent[600],
          flexShrink: 0,
          mt: 0.5,
        }}
      >
        {isUser ? (
          <PersonOutlineOutlinedIcon sx={{ fontSize: 16, color: "#fff" }} />
        ) : (
          <SmartToyOutlinedIcon sx={{ fontSize: 16, color: "#fff" }} />
        )}
      </Box>
      <Box
        sx={{
          maxWidth: "75%",
          px: 1.5,
          py: 1,
          borderRadius: isUser ? "12px 12px 2px 12px" : "12px 12px 12px 2px",
          bgcolor: isUser ? colors.greenAccent[500] : colors.blueAccent[900],
          color: "#000",
        }}
      >
        {isUser ? (
          <Typography
            variant="body2"
            sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: fontConfig?.message || "14px", lineHeight: fontConfig?.lineHeight || 1.5 }}
          >
            {message.content}
          </Typography>
        ) : (
          <Box
            sx={{
              fontSize: fontConfig?.message || "14px",
              lineHeight: fontConfig?.lineHeight || 1.5,
              wordBreak: "break-word",
              "& p": { m: 0, mb: 0.5 },
              "& p:last-child": { mb: 0 },
              "& table": {
                borderCollapse: "collapse",
                width: "100%",
                my: 1,
                fontSize: "13px",
              },
              "& th, & td": {
                border: "1px solid",
                borderColor: colors.blueAccent[700],
                px: 1,
                py: 0.5,
                textAlign: "left",
              },
              "& th": {
                fontWeight: 600,
                bgcolor: colors.blueAccent[800],
              },
              "& ul, & ol": { m: 0, pl: 2.5 },
              "& li": { mb: 0.25 },
              "& code": {
                fontSize: "12px",
                bgcolor: "rgba(255,255,255,0.08)",
                px: 0.5,
                borderRadius: "3px",
              },
            }}
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {message.content}
            </ReactMarkdown>
          </Box>
        )}
        {message.file && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<FileDownloadOutlinedIcon />}
            onClick={handleDownload}
            sx={{
              mt: 1,
              textTransform: "none",
              fontSize: fontConfig?.download || "12px",
              borderColor: colors.blueAccent[400],
              color: colors.blueAccent[300],
              "&:hover": {
                borderColor: colors.blueAccent[300],
                bgcolor: "rgba(59,130,246,0.1)",
              },
            }}
          >
            {message.file.name}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default ChatMessage;
